import React, { useState, useEffect, useCallback } from 'react';
import { colaboradoresApi, servicosApi, cidadesApi } from '../lib/api';
import { formatEndereco, formatCurrency, ESTADOS_BR, getEstadoNome } from '../lib/utils';
import { CidadeSelectOptions } from '../components/CidadeSelectOptions';
import { ColaboradorOcorrenciasDialog } from '../components/ColaboradorOcorrenciasDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Loader2, UserCog, QrCode, Star, UserX, UserCheck, KeyRound } from 'lucide-react';

const emptyEndereco = {
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: ''
};

const emptyColaborador = {
  nome: '',
  telefone: '',
  cpf: '',
  endereco: { ...emptyEndereco },
  servicos: [],
  categorias_servico: [],
  regioes_atendimento: [],
  comissao: 0,
  pix_chave: '',
  pix_tipo: ''
};

const Colaboradores = () => {
  const [colaboradores, setColaboradores] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [categoriasServico, setCategoriasServico] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [cidadeFilter, setCidadeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [formData, setFormData] = useState({ ...emptyColaborador });
  const [saving, setSaving] = useState(false);
  const [novaCidade, setNovaCidade] = useState('');
  const [novaCidadeUf, setNovaCidadeUf] = useState('MG');
  // areasForm: { [nomeCidade]: { toda_cidade: bool, zonas: [str] } }
  const [areasForm, setAreasForm] = useState({});
  const [showOcorrencias, setShowOcorrencias] = useState(false);
  const [ocorrenciasColab, setOcorrenciasColab] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ativos');
  const [acessoDialog, setAcessoDialog] = useState({ open: false, colab: null, senha: '', loading: false });

  const handleGerarAcesso = async () => {
    setAcessoDialog((p) => ({ ...p, loading: true }));
    try {
      const r = await colaboradoresApi.portalAcesso(acessoDialog.colab.id);
      setAcessoDialog((p) => ({ ...p, senha: r.data.senha, loading: false }));
      loadData();
    } catch {
      toast.error('Erro ao gerar acesso');
      setAcessoDialog((p) => ({ ...p, loading: false }));
    }
  };

  const copiarConvite = () => {
    const c = acessoDialog.colab;
    const msg = `Olá ${c.nome}! Aqui é da Tenance. 😊\n\nCriamos sua Área do Colaborador, onde você marca os serviços que realiza e mantém seus dados atualizados:\n\n🔗 Acesse: ${window.location.origin}/colaborador\n📱 Login: ${c.telefone}\n🔑 Senha: ${acessoDialog.senha}\n\nAo entrar, troque a senha na aba "Meus Dados".`;
    navigator.clipboard.writeText(msg).then(() => toast.success('Convite copiado! Cole no WhatsApp do colaborador.'));
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [colaboradoresRes, servicosRes, catRes, cidadesRes] = await Promise.all([
        colaboradoresApi.list(),
        servicosApi.list(),
        servicosApi.listCategorias(),
        cidadesApi.list()
      ]);
      setColaboradores(colaboradoresRes.data);
      setServicos(servicosRes.data);
      setCategoriasServico(catRes.data);
      setCidades(cidadesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (ocorrenciasColab) {
      const fresh = colaboradores.find((c) => c.id === ocorrenciasColab.id);
      if (fresh && fresh !== ocorrenciasColab) setOcorrenciasColab(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colaboradores]);

  const handleOpenForm = (colaborador = null) => {
    if (colaborador) {
      setSelectedColaborador(colaborador);
      setFormData({
        nome: colaborador.nome,
        telefone: colaborador.telefone,
        cpf: colaborador.cpf || '',
        endereco: colaborador.endereco || { ...emptyEndereco },
        servicos: colaborador.servicos || [],
        categorias_servico: colaborador.categorias_servico || [],
        regioes_atendimento: colaborador.regioes_atendimento || [],
        comissao: colaborador.comissao || 0,
        pix_chave: colaborador.pix_chave || '',
        pix_tipo: colaborador.pix_tipo || ''
      });
      // Prefill áreas: usa areas_atendimento; se ausente, converte legado (toda a cidade)
      const areas = {};
      if ((colaborador.areas_atendimento || []).length > 0) {
        colaborador.areas_atendimento.forEach((a) => {
          if (a.cidade) areas[a.cidade] = { toda_cidade: !!a.toda_cidade, zonas: a.zonas || [] };
        });
      } else {
        (colaborador.regioes_atendimento || []).forEach((r) => {
          areas[r] = { toda_cidade: true, zonas: [] };
        });
      }
      setAreasForm(areas);
    } else {
      setSelectedColaborador(null);
      setFormData({ ...emptyColaborador });
      setAreasForm({});
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedColaborador(null);
    setFormData({ ...emptyColaborador });
    setAreasForm({});
  };

  const handleServiceToggle = (servicoNome) => {
    setFormData(prev => ({
      ...prev,
      servicos: prev.servicos.includes(servicoNome)
        ? prev.servicos.filter(s => s !== servicoNome)
        : [...prev.servicos, servicoNome]
    }));
  };

  const handleCategoriaToggle = (catNome) => {
    setFormData(prev => ({
      ...prev,
      categorias_servico: prev.categorias_servico.includes(catNome)
        ? prev.categorias_servico.filter(c => c !== catNome)
        : [...prev.categorias_servico, catNome]
    }));
  };

  // Alterna "Toda a cidade" (para cidades sem zonas, é o único toggle)
  const toggleTodaCidade = (cidadeNome) => {
    setAreasForm((prev) => {
      const cur = prev[cidadeNome];
      if (cur?.toda_cidade) {
        const next = { ...prev };
        delete next[cidadeNome];
        return next;
      }
      return { ...prev, [cidadeNome]: { toda_cidade: true, zonas: [] } };
    });
  };

  const toggleZona = (cidadeNome, zonaNome) => {
    setAreasForm((prev) => {
      const cur = prev[cidadeNome] || { toda_cidade: false, zonas: [] };
      const zonas = cur.zonas.includes(zonaNome)
        ? cur.zonas.filter((z) => z !== zonaNome)
        : [...cur.zonas, zonaNome];
      if (zonas.length === 0 && !cur.toda_cidade) {
        const next = { ...prev };
        delete next[cidadeNome];
        return next;
      }
      return { ...prev, [cidadeNome]: { toda_cidade: false, zonas } };
    });
  };

  const handleAddCidade = async () => {
    const nome = novaCidade.trim();
    if (!nome) return;
    try {
      const res = await cidadesApi.create({ nome, estado: novaCidadeUf });
      setCidades(prev => [...prev, res.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setAreasForm(prev => ({ ...prev, [res.data.nome]: { toda_cidade: true, zonas: [] } }));
      setNovaCidade('');
      toast.success('Cidade adicionada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar cidade');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Constrói areas_atendimento estruturado + regioes_atendimento legado (nomes de cidades)
    const areas_atendimento = Object.entries(areasForm).map(([nomeCidade, a]) => {
      const cid = cidades.find((c) => c.nome === nomeCidade);
      return {
        cidade: nomeCidade,
        estado: cid?.estado || '',
        toda_cidade: !!a.toda_cidade,
        zonas: a.toda_cidade ? [] : a.zonas,
        bairros: [],
      };
    });
    const payload = {
      ...formData,
      areas_atendimento,
      regioes_atendimento: areas_atendimento.map((a) => a.cidade),
    };

    try {
      if (selectedColaborador) {
        await colaboradoresApi.update(selectedColaborador.id, payload);
        toast.success('Colaborador atualizado com sucesso!');
      } else {
        await colaboradoresApi.create(payload);
        toast.success('Colaborador criado com sucesso!');
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error('Error saving colaborador:', error);
      toast.error('Erro ao salvar colaborador');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedColaborador) return;

    try {
      await colaboradoresApi.delete(selectedColaborador.id);
      toast.success('Colaborador excluído com sucesso!');
      setShowDeleteDialog(false);
      setSelectedColaborador(null);
      loadData();
    } catch (error) {
      console.error('Error deleting colaborador:', error);
      toast.error('Erro ao excluir colaborador');
    }
  };

  // Normaliza para comparar ignorando maiúsculas e acentos
  const normalize = (v) => String(v || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Filtros baseados nas cidades CADASTRADAS no sistema + regiões de atendimento
  const estadosDisponiveis = React.useMemo(
    () => [...new Set(cidades.map((c) => c.estado || 'MG'))].sort(),
    [cidades]
  );

  const cidadesFiltro = React.useMemo(
    () => estadoFilter === 'all' ? cidades : cidades.filter((c) => (c.estado || 'MG') === estadoFilter),
    [cidades, estadoFilter]
  );

  // Cidades que o colaborador atende (estruturado ou legado)
  const cidadesAtendidas = (colaborador) => {
    const areas = colaborador.areas_atendimento || [];
    if (areas.length > 0) return areas.map((a) => a.cidade);
    return colaborador.regioes_atendimento || [];
  };

  // Estados em que o colaborador atende (via regiões de atendimento x cidades cadastradas)
  const estadosDoColaborador = (colaborador) => {
    const regs = cidadesAtendidas(colaborador).map(normalize);
    const ufs = new Set();
    cidades.forEach((c) => {
      if (regs.includes(normalize(c.nome))) ufs.add(c.estado || 'MG');
    });
    return [...ufs].sort();
  };

  const handleToggleAtivo = async (colaborador) => {
    const novoAtivo = colaborador.ativo === false;
    try {
      await colaboradoresApi.update(colaborador.id, { ativo: novoAtivo });
      setColaboradores((prev) =>
        prev.map((c) => (c.id === colaborador.id ? { ...c, ativo: novoAtivo } : c))
      );
      toast.success(novoAtivo ? `${colaborador.nome} reativado` : `${colaborador.nome} desativado`);
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleToggleFavorito = async (colaborador) => {
    try {
      await colaboradoresApi.update(colaborador.id, { favorito: !colaborador.favorito });
      setColaboradores((prev) =>
        prev.map((c) => (c.id === colaborador.id ? { ...c, favorito: !c.favorito } : c))
      );
      toast.success(!colaborador.favorito
        ? `${colaborador.nome} marcado como favorito ⭐`
        : 'Removido dos favoritos');
    } catch (err) {
      toast.error('Erro ao atualizar favorito');
    }
  };

  const filteredColaboradores = colaboradores.filter((colaborador) => {
    const ativo = colaborador.ativo !== false;
    if (statusFilter === 'ativos' && !ativo) return false;
    if (statusFilter === 'desativados' && ativo) return false;
    const q = searchTerm.toLowerCase();
    const matchText =
      colaborador.nome.toLowerCase().includes(q) ||
      colaborador.telefone.includes(searchTerm) ||
      (colaborador.cpf && colaborador.cpf.includes(searchTerm));
    if (!matchText) return false;
    const regs = cidadesAtendidas(colaborador).map(normalize);
    if (estadoFilter !== 'all') {
      const cidadesDoEstado = cidades
        .filter((c) => (c.estado || 'MG') === estadoFilter)
        .map((c) => normalize(c.nome));
      if (!regs.some((r) => cidadesDoEstado.includes(r))) return false;
    }
    if (cidadeFilter !== 'all') {
      if (!regs.includes(normalize(cidadeFilter))) return false;
    }
    return true;
  }).sort((a, b) => ((b.favorito ? 1 : 0) - (a.favorito ? 1 : 0)) || a.nome.localeCompare(b.nome));

  return (
    <div className="space-y-6" data-testid="colaboradores-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Colaboradores
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie seus prestadores de serviço
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-colaborador-btn">
          <Plus size={18} className="mr-2" />
          Novo Colaborador
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <UserCog size={20} />
              Lista de Colaboradores ({filteredColaboradores.length})
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="filter-status-colab"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativos">Ativos</SelectItem>
                  <SelectItem value="desativados">Desativados</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
              <Select value={estadoFilter} onValueChange={(v) => { setEstadoFilter(v); setCidadeFilter('all'); }}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {estadosDisponiveis.map((e) => (<SelectItem key={e} value={e}>{getEstadoNome(e)} ({e})</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={cidadeFilter} onValueChange={setCidadeFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="filter-cidade"><SelectValue placeholder="Cidade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as cidades</SelectItem>
                  <CidadeSelectOptions cidades={cidadesFiltro} currentValue={cidadeFilter !== 'all' ? cidadeFilter : ''} />
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar colaborador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-colaborador-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : filteredColaboradores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'Nenhum colaborador encontrado' : 'Nenhum colaborador cadastrado'}
            </div>
          ) : (
            <div className="table-fit">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Nota</th>
                    <th className="hidden lg:table-cell">Telefone</th>
                    <th className="hidden xl:table-cell">CPF</th>
                    <th className="hidden lg:table-cell">Categorias</th>
                    <th className="hidden lg:table-cell">Estado</th>
                    <th className="hidden lg:table-cell">Regiões</th>
                    <th>Comissão</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColaboradores.map((colaborador) => (
                    <tr key={colaborador.id} data-testid={`colaborador-row-${colaborador.id}`} className={colaborador.ativo === false ? 'opacity-50' : ''}>
                      <td className="font-medium">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleToggleFavorito(colaborador)}
                            title={colaborador.favorito ? 'Remover dos favoritos' : 'Marcar como favorito'}
                            data-testid={`fav-btn-${colaborador.id}`}
                            className="shrink-0"
                          >
                            <Star
                              size={16}
                              className={colaborador.favorito
                                ? 'fill-yellow-400 text-yellow-500'
                                : 'text-gray-300 hover:text-yellow-400'}
                            />
                          </button>
                          {colaborador.nome}
                          {colaborador.ativo === false && (
                            <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500 bg-gray-100" data-testid={`badge-desativado-${colaborador.id}`}>Desativado</Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => { setOcorrenciasColab(colaborador); setShowOcorrencias(true); }}
                          className="flex flex-col items-start gap-0.5"
                          title="Ver avaliação e ocorrências"
                          data-testid={`nota-btn-${colaborador.id}`}
                        >
                          <span className={`inline-flex items-center gap-1 font-semibold text-sm ${colaborador.alerta ? 'text-red-600' : 'text-amber-600'}`}>
                            <Star size={14} className={colaborador.alerta ? 'fill-red-500 text-red-500' : 'fill-amber-400 text-amber-500'} />
                            {Number(colaborador.nota ?? 5).toFixed(1)}
                            {colaborador.nota_manual != null && <span className="text-[10px] text-gray-400 font-normal">(manual)</span>}
                          </span>
                          {colaborador.alerta && (
                            <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1" data-testid={`alerta-colab-${colaborador.id}`}>⚠ Atenção</span>
                          )}
                          {!colaborador.alerta && (colaborador.total_ocorrencias || 0) > 0 && (
                            <span className="text-[10px] text-gray-400">{colaborador.total_ocorrencias} ocorrência{colaborador.total_ocorrencias > 1 ? 's' : ''}</span>
                          )}
                        </button>
                      </td>
                      <td className="hidden lg:table-cell">{colaborador.telefone}</td>
                      <td className="hidden xl:table-cell">{colaborador.cpf || '-'}</td>
                      <td className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(colaborador.categorias_servico || []).map((cat, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
                              {cat}
                            </Badge>
                          ))}
                          {(!colaborador.categorias_servico || colaborador.categorias_servico.length === 0) && (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1" data-testid={`estados-colaborador-${colaborador.id}`}>
                          {estadosDoColaborador(colaborador).map((uf) => (
                            <Badge key={uf} variant="outline" className="text-xs border-purple-300 text-purple-700 bg-purple-50">
                              {uf}
                            </Badge>
                          ))}
                          {estadosDoColaborador(colaborador).length === 0 && (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {((colaborador.areas_atendimento || []).length > 0
                            ? colaborador.areas_atendimento.map((a) =>
                                `${a.cidade}${!a.toda_cidade && (a.zonas || []).length ? ` (${a.zonas.join(', ')})` : ''}`)
                            : (colaborador.regioes_atendimento || [])
                          ).map((reg, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                              {reg}
                            </Badge>
                          ))}
                          {(!colaborador.regioes_atendimento || colaborador.regioes_atendimento.length === 0) && (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </div>
                      </td>
                      <td>{colaborador.comissao}%</td>
                      <td>
                        <div className="flex items-center gap-2 flex-nowrap justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(colaborador)}
                            data-testid={`edit-colaborador-${colaborador.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAcessoDialog({ open: true, colab: colaborador, senha: '', loading: false })}
                            title={colaborador.portal_acesso ? 'Acesso do portal (ativo) — gerar nova senha' : 'Criar acesso à Área do Colaborador'}
                            data-testid={`portal-acesso-${colaborador.id}`}
                          >
                            <KeyRound size={16} className={colaborador.portal_acesso ? 'text-emerald-600' : 'text-gray-400'} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAtivo(colaborador)}
                            title={colaborador.ativo === false ? 'Reativar colaborador' : 'Desativar colaborador'}
                            className={colaborador.ativo === false ? 'text-emerald-600 hover:text-emerald-700' : 'text-orange-500 hover:text-orange-600'}
                            data-testid={`toggle-ativo-${colaborador.id}`}
                          >
                            {colaborador.ativo === false ? <UserCheck size={16} /> : <UserX size={16} />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedColaborador(colaborador);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-colaborador-${colaborador.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedColaborador ? 'Editar Colaborador' : 'Novo Colaborador'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do colaborador
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="colaborador-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                  placeholder="(00) 00000-0000"
                  data-testid="colaborador-telefone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF</Label>
                <Input
                  id="cpf"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                  data-testid="colaborador-cpf-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="comissao">Comissão (%)</Label>
                <Input
                  id="comissao"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.comissao}
                  onChange={(e) => setFormData({ ...formData, comissao: parseFloat(e.target.value) || 0 })}
                  data-testid="colaborador-comissao-input"
                />
              </div>
            </div>

            {/* PIX para pagamento */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <QrCode size={18} className="text-[#2B7A3D]" />
                Chave PIX para pagamento
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pix_tipo">Tipo</Label>
                  <Select
                    value={formData.pix_tipo || undefined}
                    onValueChange={(v) => setFormData({ ...formData, pix_tipo: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger data-testid="colaborador-pix-tipo-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="telefone">Telefone</SelectItem>
                      <SelectItem value="aleatoria">Chave aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="pix_chave">Chave PIX</Label>
                  <Input
                    id="pix_chave"
                    value={formData.pix_chave}
                    onChange={(e) => setFormData({ ...formData, pix_chave: e.target.value })}
                    placeholder="Chave para receber pagamentos"
                    data-testid="colaborador-pix-chave-input"
                  />
                </div>
              </div>
            </div>

            {/* Categorias de serviço que realiza */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Categorias de serviço que realiza</h4>
              {categoriasServico.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Nenhuma categoria cadastrada. Vá em <span className="font-medium">Serviços → Gerenciar Categorias</span> para adicionar.
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {categoriasServico.map((cat) => (
                    <div key={cat.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cat-${cat.id}`}
                        checked={formData.categorias_servico.includes(cat.nome)}
                        onCheckedChange={() => handleCategoriaToggle(cat.nome)}
                        data-testid={`categoria-checkbox-${cat.id}`}
                      />
                      <label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                        {cat.nome}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Regiões de atendimento */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Regiões de atendimento</h4>
              <div className="space-y-3 mb-3">
                {[...new Set(cidades.map((c) => c.estado || 'MG'))].sort().map((uf) => (
                  <div key={uf}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5" data-testid={`regiao-grupo-${uf}`}>
                      {getEstadoNome(uf)} ({uf})
                    </p>
                    <div className="space-y-2 pl-1">
                      {cidades.filter((c) => (c.estado || 'MG') === uf).map((cidade) => {
                        const area = areasForm[cidade.nome];
                        const temZonas = (cidade.zonas || []).length > 0;
                        if (!temZonas) {
                          return (
                            <div key={cidade.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`cidade-${cidade.id}`}
                                checked={!!area?.toda_cidade}
                                onCheckedChange={() => toggleTodaCidade(cidade.nome)}
                                data-testid={`cidade-checkbox-${cidade.id}`}
                              />
                              <label htmlFor={`cidade-${cidade.id}`} className="text-sm cursor-pointer">
                                {cidade.nome}
                              </label>
                            </div>
                          );
                        }
                        return (
                          <div key={cidade.id} className="rounded-md border border-gray-200 p-2" data-testid={`cidade-zonas-bloco-${cidade.id}`}>
                            <p className="text-sm font-medium mb-1.5">{cidade.nome}</p>
                            <div className="flex items-center space-x-2 mb-1.5">
                              <Checkbox
                                id={`cidade-toda-${cidade.id}`}
                                checked={!!area?.toda_cidade}
                                onCheckedChange={() => toggleTodaCidade(cidade.nome)}
                                data-testid={`cidade-toda-checkbox-${cidade.id}`}
                              />
                              <label htmlFor={`cidade-toda-${cidade.id}`} className="text-sm cursor-pointer font-medium text-emerald-700">
                                Toda a cidade
                              </label>
                            </div>
                            <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 pl-5 ${area?.toda_cidade ? 'opacity-40 pointer-events-none' : ''}`}>
                              {cidade.zonas.map((z) => (
                                <div key={z.nome} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`zona-${cidade.id}-${z.nome}`}
                                    checked={!area?.toda_cidade && (area?.zonas || []).includes(z.nome)}
                                    onCheckedChange={() => toggleZona(cidade.nome, z.nome)}
                                    data-testid={`zona-checkbox-${cidade.id}-${z.nome}`}
                                  />
                                  <label htmlFor={`zona-${cidade.id}-${z.nome}`} className="text-sm cursor-pointer">
                                    {z.nome}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={novaCidade}
                  onChange={(e) => setNovaCidade(e.target.value)}
                  placeholder="Adicionar nova cidade..."
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCidade(); } }}
                  data-testid="nova-cidade-input"
                />
                <Select value={novaCidadeUf} onValueChange={setNovaCidadeUf}>
                  <SelectTrigger className="w-24" data-testid="nova-cidade-uf">
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BR.map((e) => (
                      <SelectItem key={e.uf} value={e.uf}>{e.uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={handleAddCidade} data-testid="add-cidade-btn">
                  Adicionar
                </Button>
              </div>
            </div>

            {/* Serviços que realiza (legado - opcional) */}
            {servicos.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 text-gray-500">Serviços específicos <span className="text-xs font-normal">(opcional)</span></h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {servicos.map((servico) => (
                    <div key={servico.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`servico-${servico.id}`}
                        checked={formData.servicos.includes(servico.nome)}
                        onCheckedChange={() => handleServiceToggle(servico.nome)}
                        data-testid={`servico-checkbox-${servico.id}`}
                      />
                      <label htmlFor={`servico-${servico.id}`} className="text-sm">
                        {servico.nome}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Endereço */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="rua">Rua</Label>
                  <Input
                    id="rua"
                    value={formData.endereco.rua}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, rua: e.target.value }
                    })}
                    data-testid="colaborador-rua-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    value={formData.endereco.numero}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, numero: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.endereco.bairro}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, bairro: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.endereco.cidade}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, cidade: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    value={formData.endereco.estado}
                    onChange={(e) => setFormData({
                      ...formData,
                      endereco: { ...formData.endereco, estado: e.target.value }
                    })}
                    maxLength={2}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-colaborador-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o colaborador &quot;{selectedColaborador?.nome}&quot;? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ColaboradorOcorrenciasDialog
        open={showOcorrencias}
        onOpenChange={setShowOcorrencias}
        colaborador={ocorrenciasColab}
        onChanged={loadData}
      />

      <Dialog open={acessoDialog.open} onOpenChange={(v) => setAcessoDialog((p) => ({ ...p, open: v }))}>
        <DialogContent className="max-w-md" data-testid="acesso-portal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-emerald-700" /> Área do Colaborador</DialogTitle>
            <DialogDescription>
              {acessoDialog.colab?.nome} — login pelo telefone <strong>{acessoDialog.colab?.telefone}</strong> em <strong>{window.location.origin}/colaborador</strong>
            </DialogDescription>
          </DialogHeader>
          {acessoDialog.senha ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                <p className="text-xs text-gray-500">Senha temporária gerada (anote, não será mostrada de novo):</p>
                <p className="text-2xl font-bold tracking-wider text-emerald-800" data-testid="senha-gerada">{acessoDialog.senha}</p>
              </div>
              <Button onClick={copiarConvite} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="copiar-convite-btn">
                Copiar convite para WhatsApp
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {acessoDialog.colab?.portal_acesso
                  ? 'Este colaborador já tem acesso. Gerar uma nova senha substitui a atual.'
                  : 'Gere uma senha temporária e envie o convite — ele poderá marcar os serviços que realiza, atualizar contato e PIX, e ver as OS dele.'}
              </p>
              <Button onClick={handleGerarAcesso} disabled={acessoDialog.loading} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="gerar-acesso-btn">
                {acessoDialog.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (acessoDialog.colab?.portal_acesso ? 'Gerar nova senha' : 'Gerar acesso')}
              </Button>
              {acessoDialog.colab?.portal_acesso && (
                <Button variant="outline" onClick={async () => { await colaboradoresApi.removePortalAcesso(acessoDialog.colab.id); toast.success('Acesso removido'); setAcessoDialog({ open: false, colab: null, senha: '', loading: false }); loadData(); }} className="w-full text-red-600 border-red-200" data-testid="remover-acesso-btn">
                  Remover acesso
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Colaboradores;
